import { getDefaultMarketCountryCode } from '../services/marketMetadata'

export const getPermissionTarget = permission => {
	if (typeof permission === 'string') {
		const separatorIndex = permission.indexOf(':')
		return separatorIndex === -1 ? '' : permission.slice(separatorIndex + 1)
	}

	return permission?.target || ''
}

export const getTargetAttributes = target => {
	const [resourceType, scope, serializedAttributes] = target.split(':')

	if (resourceType !== 'merchant' || scope !== 'attr' || !serializedAttributes) {
		return null
	}

	return serializedAttributes.split(',').reduce((attributes, item) => {
		const separatorIndex = item.indexOf('=')
		if (separatorIndex === -1) {
			return attributes
		}

		const key = item.slice(0, separatorIndex)
		const value = item.slice(separatorIndex + 1)

		if (!key || !value) {
			return attributes
		}

		return {
			...attributes,
			[key]: value,
		}
	}, {})
}

export const getPermissionsV2 = decodedToken => decodedToken.permissions_v2 || decodedToken.permission_v2 || []

export const getAllowedMerchantCountries = permissionsV2 => {
	const countries = permissionsV2.reduce((result, permission) => {
		const attributes = getTargetAttributes(getPermissionTarget(permission))
		const country = attributes?.country

		if (!country || result.includes(country)) {
			return result
		}

		return [...result, country]
	}, [])

	return countries.sort((left, right) => left.localeCompare(right))
}

export const getPermissionOperation = permission => {
	if (typeof permission === 'string') {
		const i = permission.indexOf(':')
		return i === -1 ? permission : permission.slice(0, i)
	}
	return permission?.operation || ''
}

export const merchantPermissionMatches = ({ permission, operation, currentMerchantID, currentMerchantCountryCode }) => {
	if (getPermissionOperation(permission) !== operation) return false

	const target = getPermissionTarget(permission)
	if (target === 'merchant:*') return true
	if (currentMerchantID && target === `merchant:${currentMerchantID}`) return true

	const attributes = getTargetAttributes(target)
	return !!currentMerchantCountryCode && attributes?.country === currentMerchantCountryCode
}

export const hasTargetPermission = ({ permissions, operation, target }) =>
	permissions.some(permission => getPermissionOperation(permission) === operation && getPermissionTarget(permission) === target)

export const getCreateMerchantCountries = permissionsV2 =>
	getAllowedMerchantCountries(permissionsV2.filter(p => getPermissionOperation(p) === 'create-merchant'))

export const getViewInvoiceRequestCountries = permissionsV2 =>
	getAllowedMerchantCountries(permissionsV2.filter(p => getPermissionOperation(p) === 'can-view-all-invoice-request'))

export const getSpecificMerchantIdsFromPermissionsV2 = permissionsV2 => {
	return permissionsV2.reduce((result, permission) => {
		const target = getPermissionTarget(permission)
		if (!target.startsWith('merchant:')) return result
		const merchantId = target.split(':')[1]
		if (!merchantId || merchantId === '*' || merchantId === 'attr') return result
		return result.includes(merchantId) ? result : [...result, merchantId]
	}, [])
}

export const getMerchantSearchCountry = ({ allowedMerchantCountries, currentMerchantCountryCode, myMerchants = [] }) => {
	if (
		currentMerchantCountryCode &&
		(allowedMerchantCountries.length === 0 || allowedMerchantCountries.includes(currentMerchantCountryCode))
	) {
		return currentMerchantCountryCode
	}

	if (allowedMerchantCountries.length > 0) {
		return allowedMerchantCountries[0]
	}

	return myMerchants.find(merchant => merchant.country)?.country || getDefaultMarketCountryCode()
}
